/**
 * Générateur — Automatisme : construire le symétrique, par rapport à un axe,
 * d'un point, d'une figure, sur feuille blanche.
 * Attendu ID : 1071 | Niveau : 5ème | Domaine : Transformations
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Les deux gestes        (QCM étapes / instruments ; texte à trous ; V/F)
 *   - Le bon tracé           (QCM planche ①-④)
 *   - Lire la construction   (QCM H / médiatrice / point de l'axe / point I ; saisie longueurs ; V/F figure)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°33 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1 (compléter sur quadrillage), CONSOLIDÉ au CM2
 * (construire sur quadrillage), ÉTENDU et FORMALISÉ en 6e (papier uni,
 * définition du symétrique, propriétés, médiatrice), automatisme en 5e.
 * P2 : sens installés — pliage, report de carreaux ; sens de 6e — la
 * définition en DEUX GESTES : perpendiculaire à l'axe, même distance de
 * l'autre côté, donc (d) médiatrice de [MM′]. Sens non installé : la symétrie
 * centrale. Distracteurs : la parallèle à l'axe, le report du même côté, le
 * demi-tour, le rapporteur.
 * P3 : longueurs en cm entiers (1 à 5), doubles et moitiés. P5 : figure sur
 * feuille blanche, sans quadrillage ni repère ; perpendiculaire tracée, codage
 * de l'angle droit et des longueurs égales. P6 : équerre et compas (règle
 * graduée en remplacement du compas), pas de rapporteur, pas de calculatrice.
 * P7 : pas de symétrie centrale, pas d'axe de symétrie d'une figure, la mesure
 * n'est pas une preuve. P8 : symétrique, axe, perpendiculaire, pied de la
 * perpendiculaire, médiatrice, M′, (MH) ⊥ (d). P9 : perpendiculaire à
 * l'équerre, report au compas, médiatrice (1078), conservations (1070) restent
 * implicites. P10 : vérification par pliage ou lecture du codage ; consignes
 * « quelle étape », « que représente », « combien mesure ». P11 : rupture du
 * comptage de carreaux vers les deux gestes instrumentés ; le point de l'axe
 * est son propre symétrique ; un segment qui coupe l'axe a un symétrique qui
 * passe par le même point. P12 : rémanence forte, pas de réactivation.
 * INVARIANTS :
 *   - toute figure est CALCULÉE (axe = point + direction, symétrique exact,
 *     pied de la perpendiculaire exact) ; tout tient dans le cadre avec marge ;
 *   - une seule bonne réponse par QCM, options textuellement distinctes ;
 *   - V/F : jamais de crochets dans une ligne (clé de correction), mixité
 *     1-3 faux, fenêtre stricte des dernières lignes fixes servies ;
 *   - data-axe, data-points, data-scale, data-candidat dans le SVG pour les
 *     solveurs ; réponses ENTIÈRES ; pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1071;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_GESTES = 'Les deux gestes';
const L_TRACE = 'Le bon tracé';
const L_LECTURE = 'Lire la construction';

const PRIME = '′';
const BLEU = '#1E3A8A'; const ORANGE = '#B45309'; const VERT = '#15803d'; const ROUGE = '#dc2626';

// ─── Géométrie sur feuille blanche ─────────────────────────────────────────
// L'axe (d) passe par C avec la direction unitaire u ; n est la normale.
// Un point à distance s (signée, en px) de l'axe : P = C + t·u + s·n.

const W = 320; const H = 220; const SCALE = 16; const MARGE = 18;
const ANGLES = [0, 90, 25, 155];
const rad = (deg) => (deg * Math.PI) / 180;
const f1 = (v) => v.toFixed(1);
const add = (P, v, k = 1) => [P[0] + k * v[0], P[1] + k * v[1]];
const sub = (P, Q) => [P[0] - Q[0], P[1] - Q[1]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1];
const norme = (v) => Math.hypot(v[0], v[1]);
const unit = (v) => { const l = norme(v) || 1; return [v[0] / l, v[1] / l]; };
const cle = (P) => `${f1(P[0])},${f1(P[1])}`;
const cleU = (u) => `${u[0].toFixed(4)},${u[1].toFixed(4)}`;   // direction de l'axe, précise pour les solveurs

const repere = (theta) => { const u = [Math.cos(rad(theta)), -Math.sin(rad(theta))]; return { u, n: [-u[1], u[0]] }; };
const dansCadre = (P, w = W, h = H, m = MARGE) => P[0] >= m && P[0] <= w - m && P[1] >= m && P[1] <= h - m;

/** Extrémités de l'axe dans le cadre (w × h), à 4 px du bord. */
const boutsAxe = (C, u, w, h) => {
    const ts = [];
    const bornes = [[0, 4], [0, w - 4], [1, 4], [1, h - 4]];
    bornes.forEach(([i, val]) => { if (Math.abs(u[i]) > 1e-9) ts.push((val - C[i]) / u[i]); });
    const dedans = (t) => { const P = add(C, u, t); return P[0] >= 3.9 && P[0] <= w - 3.9 && P[1] >= 3.9 && P[1] <= h - 3.9; };
    const ok = ts.filter(dedans).sort((a, b) => a - b);
    return [add(C, u, ok[0]), add(C, u, ok[ok.length - 1])];
};

/**
 * Rendu d'une « feuille » : axe, segments, angles droits, codages, textes, points.
 * cfg = { C, u, n, w, h, segments: [{a, b, couleur, dash, ep}], angleDroit: [{S, d1, d2}],
 *         ticks: [{a, b, nb}], textes: [{p, txt, couleur, taille, anchor}],
 *         points: [{p, nom, couleur, decal}], nomAxe }
 */
const dessinerFeuille = (cfg, ox = 0, oy = 0) => {
    const { C, u, n, w, h } = cfg; const X = (P) => f1(ox + P[0]); const Y = (P) => f1(oy + P[1]);
    let s = '';
    const [a1, a2] = boutsAxe(C, u, w, h);
    s += `<line x1="${X(a1)}" y1="${Y(a1)}" x2="${X(a2)}" y2="${Y(a2)}" stroke="${ROUGE}" stroke-width="2.2" data-axe="${cle(C)};${cleU(u)}"/>`;
    if (cfg.nomAxe !== false) {
        // (d) près de l'extrémité la plus ÉLOIGNÉE des points (cfg.loinDe, sinon C), décalé du côté demandé
        const cote = cfg.coteNomAxe || 1;
        const ref = cfg.loinDe || C;
        const [loin, versLoin] = norme(sub(a1, ref)) > norme(sub(a2, ref)) ? [a1, -1] : [a2, 1];
        const E = add(add(loin, u, -14 * versLoin), n, 11 * cote);
        s += `<text x="${X(E)}" y="${f1(oy + E[1] + 4)}" font-size="${cfg.petit ? 11 : 13}" font-weight="700" fill="${ROUGE}" text-anchor="middle">(d)</text>`;
    }
    (cfg.segments || []).forEach((sg) => {
        s += `<line x1="${X(sg.a)}" y1="${Y(sg.a)}" x2="${X(sg.b)}" y2="${Y(sg.b)}" stroke="${sg.couleur || BLEU}" stroke-width="${sg.ep || 2}"${sg.dash ? ' stroke-dasharray="5 3"' : ''}${sg.role ? ` data-role="${sg.role}"` : ''}/>`;
    });
    (cfg.angleDroit || []).forEach((ad) => {
        const k = cfg.petit ? 6 : 8; const A = add(ad.S, ad.d1, k); const B = add(add(ad.S, ad.d1, k), ad.d2, k); const D = add(ad.S, ad.d2, k);
        s += `<polyline points="${X(A)},${Y(A)} ${X(B)},${Y(B)} ${X(D)},${Y(D)}" fill="none" stroke="#475569" stroke-width="1.4" data-angle-droit="1"/>`;
    });
    (cfg.ticks || []).forEach((tk) => {
        const m = add(tk.a, sub(tk.b, tk.a), 0.5); const d = unit(sub(tk.b, tk.a)); const p = [-d[1], d[0]]; const L = cfg.petit ? 4 : 5;
        const nb = tk.nb || 1;
        for (let i = 0; i < nb; i++) {
            const c = add(m, d, (i - (nb - 1) / 2) * 4);
            s += `<line x1="${X(add(c, p, -L))}" y1="${Y(add(c, p, -L))}" x2="${X(add(c, p, L))}" y2="${Y(add(c, p, L))}" stroke="${tk.couleur || '#475569'}" stroke-width="1.8" data-tick="${nb}"/>`;
        }
    });
    (cfg.textes || []).forEach((tx) => {
        s += `<text x="${X(tx.p)}" y="${f1(oy + tx.p[1] + (tx.taille || 11) * 0.35)}" font-size="${tx.taille || (cfg.petit ? 10 : 11.5)}" font-weight="${tx.gras ? 700 : 600}" fill="${tx.couleur || '#334155'}" text-anchor="${tx.anchor || 'middle'}">${tx.txt}</text>`;
    });
    (cfg.points || []).forEach((pt) => {
        const col = pt.couleur || BLEU; const L = add(pt.p, pt.decal || [0, -10]);
        s += `<circle cx="${X(pt.p)}" cy="${Y(pt.p)}" r="${cfg.petit ? 3 : 3.6}" fill="${col}" data-point="${pt.nom}" data-xy="${cle(pt.p)}"/>`;
        s += `<text x="${X(L)}" y="${f1(oy + L[1] + 4.5)}" font-size="${cfg.petit ? 11 : 13}" font-weight="700" fill="${col}" text-anchor="middle">${pt.nom}</text>`;
    });
    return s;
};

const svgFeuille = (cfg, extra = '') => {
    const w = cfg.w || W; const h = cfg.h || H;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial" data-scale="${SCALE}" data-points="${(cfg.points || []).map((pt) => `${pt.nom}:${cle(pt.p)}`).join('|')}"${extra}>${dessinerFeuille({ ...cfg, w, h })}</svg>`;
};

/** Étiquette d'un point, à l'opposé d'un autre point (11 px « vers l'extérieur »). */
const decalOppose = (P, depuis, dist = 12) => { const d = unit(sub(P, depuis)); return [d[0] * dist, d[1] * dist]; };

/**
 * Configuration « un point M et son pied H sur l'axe » : axe d'angle theta,
 * M à k cm (côté aléatoire), M′ le symétrique exact. Retirage tant que tout
 * ne tient pas dans le cadre (avec marge pour les étiquettes).
 */
const tirerConfig = (k, contraintes = {}) => {
    for (let essai = 0; essai < 400; essai++) {
        const theta = contraintes.theta ?? randomChoice(ANGLES);
        const { u, n } = repere(theta);
        const C = [W / 2 + randInt(-12, 12), H / 2 + randInt(-8, 8)];
        const t = randInt(-40, 40); const cote = randomChoice([1, -1]);
        const Hp = add(C, u, t); const M = add(Hp, n, cote * k * SCALE); const Mp = add(Hp, n, -cote * k * SCALE);
        const marge = contraintes.marge || 22;
        if (![M, Mp, Hp].every((P) => dansCadre(P, W, H, marge))) continue;
        return { theta, u, n, C, t, cote, H: Hp, M, Mp, k };
    }
    throw new Error('gen_1071 : configuration introuvable');
};

// ─── Pièces de figure réutilisées ──────────────────────────────────────────

/** Figure complète : M, H, M′, perpendiculaire, angle droit, codages (et longueurs en cm si demandé). */
const figureConstruction = (cfg, o = {}) => {
    const { u, n, H: Hp, M, Mp, cote, k } = cfg;
    const nM = unit(sub(M, Hp));                // de H vers M
    const nomM = o.nomM || 'M'; const nomMp = o.nomMp || `${nomM}${PRIME}`;
    const segments = [{ a: M, b: Mp, couleur: '#64748b', dash: true, ep: 1.6, role: 'perpendiculaire' }];
    const angleDroit = [{ S: Hp, d1: u, d2: nM }];
    const ticks = o.sansTicks ? [] : [{ a: Hp, b: M }, { a: Hp, b: Mp }];
    const points = [
        { p: M, nom: nomM, couleur: BLEU, decal: decalOppose(M, Hp) },
        { p: Mp, nom: nomMp, couleur: ORANGE, decal: decalOppose(Mp, Hp) },
    ];
    if (o.avecH !== false) points.push({ p: Hp, nom: o.nomH || 'H', couleur: VERT, decal: add([0, 0], add(u, nM, 1), -1) .map((v) => v * 9) });
    const textes = [];
    if (o.longueurs) {
        // « k cm » à côté du milieu de HM (et de HM′ si demandé), décalé le long de l'axe (−u),
        // plus loin quand l'axe est horizontal (le texte s'étale le long de u)
        const dec = -(15 + 8 * Math.abs(u[0]));
        textes.push({ p: add(add(Hp, nM, k * SCALE / 2), u, dec), txt: `${o.longueurs.HM ?? k} cm`, couleur: BLEU, gras: true });
        if (o.longueurs.HMp !== undefined) textes.push({ p: add(add(Hp, nM, -k * SCALE / 2), u, dec), txt: `${o.longueurs.HMp} cm`, couleur: ORANGE, gras: true });
    }
    return { C: cfg.C, u, n, segments, angleDroit, ticks, points, textes, coteNomAxe: -cote };
};

// ─── Carte 1a : les deux gestes (QCM) ──────────────────────────────────────

// Longueurs des options homogénéisées (revue 2026-09-04, A2 : la bonne était la plus longue à 80 %).
const QUESTIONS_GESTES = [
    {
        id: 'etape1', figure: 'M',
        q: 'Sur feuille blanche, on veut construire M′, symétrique de M par rapport à la droite (d). Quelle est la première étape ?',
        bonne: 'Tracer, à l\'équerre, la perpendiculaire à (d) passant par M.',
        leurres: ['Tracer, à la règle, la parallèle à (d) passant par M.', 'Reporter, au compas, la distance de M à (d) de l\'autre côté.', 'Mesurer, au rapporteur, l\'angle entre (d) et la feuille.'],
    },
    {
        id: 'etape2', figure: 'MH',
        q: 'La perpendiculaire à (d) passant par M coupe (d) en H. Quelle est la deuxième étape pour obtenir M′ ?',
        bonne: 'Reporter, au compas, la longueur HM de l\'autre côté de (d).',
        leurres: ['Reporter, au compas, la longueur HM du même côté de (d) que M.', 'Tracer, à l\'équerre, la parallèle à (d) passant par le point H.', 'Placer M′ sur la droite (d), à la distance HM du point H.'],
    },
    {
        id: 'instruments', figure: null,
        q: 'Quels instruments utilise-t-on pour construire le symétrique d\'un point sur feuille blanche ?',
        bonne: 'L\'équerre pour la perpendiculaire, le compas pour la longueur.',
        leurres: ['Le rapporteur pour l\'angle, la règle pour tracer la parallèle.', 'Le compas seul, en traçant un cercle de centre M passant par (d).', 'La règle seule, en prolongeant la droite (d) de l\'autre côté.'],
    },
    {
        id: 'verifier', figure: null,
        // « Sur feuille blanche » dans l'énoncé : sans lui, compter les carreaux était une vérification
        // légitime (retour prof 2026-09-04). Le leurre est devenu le calque GLISSÉ (le geste juste le retourne).
        q: 'Sur feuille blanche, comment vérifier qu\'une construction de symétrique est juste, sans faire de nouvelle mesure ?',
        bonne: 'En pliant la feuille le long de (d) : figure et symétrique se superposent.',
        leurres: ['En décalquant la figure et en faisant glisser le calque de l\'autre côté de (d).', 'En traçant la parallèle à (d) passant par le symétrique construit.', 'En vérifiant que la figure et son symétrique sont du même côté de (d).'],
    },
    {
        id: 'figure', figure: null,
        q: 'Sur feuille blanche, comment construit-on le symétrique d\'une figure, un triangle par exemple ?',
        bonne: 'Sommet par sommet : on construit chaque symétrique, puis on relie.',
        leurres: ['On décalque le triangle et on le fait glisser de l\'autre côté de (d).', 'On construit le symétrique d\'un seul sommet, puis on recopie le triangle.', 'On trace la parallèle à (d) passant par chaque sommet du triangle.'],
    },
];

let derniereQuestionGestes1071 = null;

const svgGestes = (type) => {
    const cfg = tirerConfig(randInt(2, 4));
    const { u, H: Hp, M, Mp, cote } = cfg;
    const nM = unit(sub(M, Hp));
    const base = { C: cfg.C, u, n: cfg.n, points: [{ p: M, nom: 'M', couleur: BLEU, decal: decalOppose(M, Hp) }], coteNomAxe: -cote };
    if (type === 'M') return svgFeuille(base);
    // perpendiculaire tracée jusqu'au-delà de l'axe (côté M′, en pointillés), H et l'angle droit
    return svgFeuille({
        ...base,
        segments: [{ a: M, b: add(Hp, nM, -0.45 * norme(sub(M, Hp))), couleur: '#64748b', dash: true, ep: 1.6, role: 'perpendiculaire' }],
        angleDroit: [{ S: Hp, d1: u, d2: nM }],
        points: [...base.points, { p: Hp, nom: 'H', couleur: VERT, decal: add(u, nM, 1).map((v) => -v * 9) }],
    });
};

const genGestesQcm = () => {
    let i = randInt(0, QUESTIONS_GESTES.length - 1);
    if (i === derniereQuestionGestes1071) i = (i + 1) % QUESTIONS_GESTES.length;
    derniereQuestionGestes1071 = i;
    const Q = QUESTIONS_GESTES[i];
    const options = shuffle([Q.bonne, ...Q.leurres]);
    const extra = Q.figure ? { elements_additionnels: imgElement(svgGestes(Q.figure), 'medium') } : {};
    const payload = makeQcm(options, Q.bonne, `<p>${Q.q}</p>`, { ...opts(L_GESTES), ...extra });
    payload.contenu = { ...payload.contenu, sousType: Q.id };
    payload._meta.params = { question: Q.id, signature: Q.id };
    return payload;
};

// ─── Carte 1b : les deux gestes (texte à trous) ────────────────────────────

const PHRASES_GESTES = [
    // L'article est DANS le trou et les leurres ont la même forme (revue 2026-09-04, A2 : « À l'compas » trahissait la réponse).
    { phrase: '{{À l\'équerre}}, on trace la {{perpendiculaire}} à (d) passant par M : elle coupe (d) en H.', distracteurs: ['Au compas', 'parallèle'] },
    { phrase: '{{Au compas}}, on reporte la longueur HM {{de l\'autre côté}} de (d), sur la perpendiculaire : on obtient M′.', distracteurs: ['À l\'équerre', 'du même côté'] },
    { phrase: 'Le point H est le {{pied}} de la perpendiculaire à (d) passant par M ; c\'est aussi le {{milieu}} du segment [MM′].', distracteurs: ['sommet', 'centre'] },
    { phrase: 'La droite (d) est la {{médiatrice}} du segment [MM′] : elle lui est {{perpendiculaire}} et passe par son milieu.', distracteurs: ['bissectrice', 'parallèle'] },
    { phrase: 'Le symétrique d\'un point situé sur l\'axe (d) est {{le point lui-même}} : sa distance à l\'axe est {{nulle}}.', distracteurs: ['un point de l\'autre côté', 'doublée'] },
    { phrase: 'Pour vérifier la construction sans mesurer, on {{plie}} la feuille le long de (d) : la figure et son symétrique {{se superposent}}.', distracteurs: ['découpe', 's\'éloignent'] },
    { phrase: 'Sur feuille blanche, le symétrique d\'une figure se construit {{point par point}} : on construit le symétrique de chaque {{sommet}}, puis on relie.', distracteurs: ['d\'un seul coup', 'milieu'] },
];
PHRASES_GESTES.forEach((P) => {
    const attendus = [...P.phrase.matchAll(/\{\{([^}]+)\}\}/g)].map((m) => m[1]);
    if (attendus.length !== 2) throw new Error('gen_1071 : phrase à trous mal formée');
    if (P.distracteurs.some((d) => attendus.includes(d))) throw new Error('gen_1071 : distracteur égal à un mot attendu');
});

let dernierePhraseGestes1071 = null;

const genGestesTrou = () => {
    let i = randInt(0, PHRASES_GESTES.length - 1);
    if (i === dernierePhraseGestes1071) i = (i + 1) % PHRASES_GESTES.length;
    dernierePhraseGestes1071 = i;
    const P = PHRASES_GESTES[i];
    const payload = makeTexteTrou(P.phrase, '<p>Construction du symétrique sur feuille blanche : complète la phrase avec les mots proposés.</p>', {
        ...opts(L_GESTES), mode: 'drag', distracteurs: shuffle(P.distracteurs), strict: false,
    });
    payload._meta.params = { phrase: i, signature: String(i) };
    return payload;
};

// ─── Carte 1c : les deux gestes (V/F) ──────────────────────────────────────

const POOL_GESTES = [
    { l: 'Sur feuille blanche, on construit le symétrique d\'un point avec l\'équerre et le compas.', v: true },
    { l: 'La perpendiculaire à l\'axe passant par M coupe l\'axe au milieu du segment MM′.', v: true },
    { l: 'L\'axe de symétrie est la médiatrice du segment qui joint un point à son symétrique.', v: true },
    { l: 'Pour vérifier une construction sans mesurer, on plie la feuille le long de l\'axe.', v: true },
    { l: 'Le symétrique d\'une figure se construit point par point, sommet après sommet.', v: true },
    { l: 'Un point situé sur l\'axe est son propre symétrique : il n\'y a rien à construire.', v: true },
    { l: 'Sur feuille blanche, on trace la parallèle à l\'axe passant par M pour construire M′.', v: false },
    { l: 'On reporte la longueur HM du même côté de l\'axe que le point M.', v: false },
    { l: 'Le rapporteur est indispensable pour construire le symétrique d\'un point.', v: false },
    { l: 'Le symétrique d\'un point situé sur l\'axe est un point situé de l\'autre côté de l\'axe.', v: false },
    { l: 'Sur feuille blanche, on compte les carreaux pour placer le symétrique d\'un point.', v: false },
    { l: 'Le symétrique d\'un segment est un segment deux fois plus long.', v: false },
];
if (POOL_GESTES.filter((it) => it.v).length !== 6) throw new Error('gen_1071 : pool V/F gestes déséquilibré');
POOL_GESTES.forEach((it) => { if (/[[\]]/.test(it.l)) throw new Error('gen_1071 : crochets dans une ligne V/F'); });

let derniersServisGestes1071 = [];

const genGestesVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        const vraie1 = Math.random() < 0.5; const k1 = randInt(1, 5);
        const vraie2 = Math.random() < 0.5; const k2 = randInt(1, 5);
        const numeriques = [
            { l: `M est à ${k1} cm de l'axe : son symétrique M${PRIME} est à ${vraie1 ? k1 : k1 + randomChoice([1, 2])} cm de l'axe, de l'autre côté.`, v: vraie1 },
            { l: `M est à ${k2} cm de l'axe : la longueur MM${PRIME} vaut ${vraie2 ? 2 * k2 : 2 * k2 + randomChoice([-1, 1, 2])} cm.`, v: vraie2 },
        ];
        const dispo = POOL_GESTES.filter((it) => !derniersServisGestes1071.includes(it.l));
        const fixes = shuffle(dispo).slice(0, 2);
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisGestes1071 = [...derniersServisGestes1071, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Construction du symétrique sur feuille blanche. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_GESTES),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1071 : V/F gestes sans mixité après 60 essais');
};

// ─── Carte 2 : le bon tracé ────────────────────────────────────────────────
// Un point M, l'axe (d), un point candidat M′ avec le tracé qui va avec :
//   symetrique : perpendiculaire, même distance, autre côté (angle droit + codages)
//   distance   : perpendiculaire, autre côté, distance différente (angle droit, longueurs écrites)
//   decale     : même distance, autre côté, mais pas sur la perpendiculaire (codages, pas d'angle droit)
//   meme_cote  : même distance, sur la perpendiculaire, mais du même côté (glissé le long de l'axe)
const ROLES_TRACE = ['symetrique', 'distance', 'decale', 'meme_cote'];

const candidatTrace = (cfg, role, o = {}) => {
    const { u, n, H: Hp, M, cote, k } = cfg; const nM = unit(sub(M, Hp));
    if (role === 'symetrique') return { p: cfg.Mp, k2: k };
    if (role === 'distance') { const k2 = o.k2 || (k + randomChoice(k >= 2 ? [-1, 1] : [1])); return { p: add(Hp, nM, -k2 * SCALE), k2 }; }
    if (role === 'decale') { const s = o.dec || randomChoice([1, -1]) * randInt(2, 3) * SCALE; return { p: add(add(Hp, u, s), nM, -k * SCALE), k2: k }; }
    // meme_cote : même distance à l'axe, même côté, décalé le long de l'axe
    const s = o.dec || randomChoice([1, -1]) * randInt(3, 4) * SCALE;
    return { p: add(add(Hp, u, s), nM, k * SCALE), k2: k, cote };
};

/**
 * Figure d'un candidat, avec le tracé cohérent avec sa géométrie : les
 * longueurs écrites et les codages disent ce qui est VRAI sur la figure.
 *   - M′ sur la perpendiculaire issue de M : une seule perpendiculaire, H, angle droit ;
 *   - M′ ailleurs : deux perpendiculaires (pieds H et H′), chacune avec son angle droit.
 */
const figureCandidat = (cfg, role, Mp) => {
    const { u, n, H: Hp, M, k } = cfg; const nM = unit(sub(M, Hp));
    const sMp = dot(sub(Mp, Hp), nM);              // position signée de M′ (négatif = autre côté)
    const tMp = dot(sub(Mp, Hp), u);               // décalage le long de l'axe
    const perp = Math.abs(tMp) < 1e-6;
    const k2 = Math.round(Math.abs(sMp) / SCALE);
    // « k cm » : plus loin du segment quand l'axe est horizontal (le texte s'étale le long de u)
    const dec = 14 + 8 * Math.abs(u[0]);
    const points = [
        { p: M, nom: 'M', couleur: BLEU, decal: decalOppose(M, Hp) },
        { p: Mp, nom: `M${PRIME}`, couleur: ORANGE, decal: nM.map((v) => v * Math.sign(sMp) * 12) },
    ];
    const segments = []; const angleDroit = []; const ticks = []; const textes = [];
    const coteEtiquetteM = perp ? -1 : -Math.sign(tMp);   // « k cm » à l'opposé de l'autre perpendiculaire
    textes.push({ p: add(add(Hp, nM, k * SCALE / 2), u, coteEtiquetteM * dec), txt: `${k} cm`, couleur: BLEU, gras: true });
    angleDroit.push({ S: Hp, d1: u, d2: nM });
    // étiquette du pied dans le quadrant (+u, côté opposé à son point) : ni le codage d'angle droit, ni les longueurs
    points.push({ p: Hp, nom: 'H', couleur: VERT, decal: [(u[0] - nM[0]) * 9, (u[1] - nM[1]) * 9] });
    if (perp) {
        segments.push({ a: M, b: Mp, couleur: '#64748b', dash: true, ep: 1.6, role: 'trace' });
        textes.push({ p: add(add(Hp, nM, sMp / 2), u, -dec), txt: `${k2} cm`, couleur: ORANGE, gras: true });
        if (k2 === k) ticks.push({ a: Hp, b: M }, { a: Hp, b: Mp });
    } else {
        const Hq = add(Hp, u, tMp); const nQ = unit(sub(Mp, Hq));
        segments.push({ a: M, b: Hp, couleur: '#64748b', dash: true, ep: 1.6, role: 'trace' }, { a: Mp, b: Hq, couleur: '#64748b', dash: true, ep: 1.6, role: 'trace' });
        angleDroit.push({ S: Hq, d1: u, d2: nQ });
        points.push({ p: Hq, nom: `H${PRIME}`, couleur: VERT, decal: [(u[0] - nQ[0]) * 9, (u[1] - nQ[1]) * 9] });
        textes.push({ p: add(add(Hq, nM, sMp / 2), u, Math.sign(tMp) * dec), txt: `${k2} cm`, couleur: ORANGE, gras: true });
        if (k2 === k) ticks.push({ a: Hp, b: M }, { a: Hq, b: Mp });
    }
    // (d) à l'extrémité la plus éloignée des deux pieds
    const loinDe = perp ? Hp : add(Hp, u, tMp / 2);
    return { C: cfg.C, u, n, segments, angleDroit, ticks, points, textes, coteNomAxe: -cfg.cote, loinDe, role };
};

const svgPlancheTrace = (cfg, panneaux) => {
    const w = W_PAN; const h = H_PAN;
    const numeros = ['①', '②', '③', '④'];
    let s = '';
    panneaux.forEach((pan, i) => {
        const ox = (i % 2) * w; const oy = Math.floor(i / 2) * (h + 4);
        s += `<g data-index="${i + 1}" data-candidat="${cle(pan.Mp)}" data-role="${pan.role}">`
            + `<rect x="${ox + 1}" y="${oy + 1}" width="${w - 2}" height="${h - 2}" fill="#fff" stroke="#e2e8f0"/>`
            + `<text x="${ox + 10}" y="${oy + 17}" font-size="15" font-weight="800" fill="${BLEU}">${numeros[i]}</text>`
            + dessinerFeuille({ ...pan.fig, w, h, petit: true }, ox, oy)
            + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * w} ${2 * h + 4}" width="${2 * w}" height="${2 * h + 4}" font-family="Arial" data-scale="${SCALE}" data-points="M:${cle(cfg.M)}|H:${cle(cfg.H)}" data-axe="${cle(cfg.C)};${cleU(cfg.u)}">${s}</svg>`;
};

const W_PAN = W / 2 + 20; const H_PAN = H / 2 + 50;   // panneaux 180 × 160

/** Configuration réduite pour les panneaux, à type d'axe IMPOSÉ (tiré d'abord, revue 2026-09-04, A8). */
const tirerConfigPanneau = (k, theta) => {
    const w = W_PAN; const h = H_PAN;
    for (let essai = 0; essai < 400; essai++) {
        const { u, n } = repere(theta);
        const C = [w / 2 + randInt(-6, 6), h / 2 + 4 + randInt(-4, 4)];
        const t = randInt(-14, 14); const cote = randomChoice([1, -1]);
        const Hp = add(C, u, t); const M = add(Hp, n, cote * k * SCALE); const Mp = add(Hp, n, -cote * k * SCALE);
        const cfg = { theta, u, n, C, t, cote, H: Hp, M, Mp, k };
        // tous les candidats doivent tenir dans le panneau
        const cands = ROLES_TRACE.map((r) => candidatTrace(cfg, r));
        if (![M, Mp, ...cands.map((c) => c.p)].every((P) => dansCadre(P, w, h, 22))) continue;
        return { cfg, cands };
    }
    return null;
};

let derniereSignatureTrace1071 = null;

const genTraceQcm = () => {
    let payload = construireTraceQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureTrace1071; t++) payload = construireTraceQcm();
    derniereSignatureTrace1071 = payload._meta.params.signature;
    return payload;
};

const construireTraceQcm = () => {
    const theta = randomChoice(ANGLES);
    let tir = null;
    for (let essai = 0; essai < 40 && !tir; essai++) tir = tirerConfigPanneau(randInt(2, 3), theta);
    if (!tir) throw new Error('gen_1071 : planche introuvable');
    const { cfg, cands } = tir;
    const panneaux = shuffle(ROLES_TRACE.map((role, i) => ({ role, Mp: cands[i].p, fig: figureCandidat(cfg, role, cands[i].p) })));
    const bonneIndex = panneaux.findIndex((p) => p.role === 'symetrique');
    const options = ['Figure ①', 'Figure ②', 'Figure ③', 'Figure ④'];
    const bonne = options[bonneIndex];
    const texte = '<p>Sur chaque figure, M est le même point et (d) la même droite. Les longueurs écrites et les codages disent ce qui est vrai sur chaque figure. Sur une seule figure, le point M′ est le symétrique de M par rapport à (d).</p><p>Laquelle ?</p>';
    const payload = makeQcm(options, bonne, texte, {
        ...opts(L_TRACE), elements_additionnels: imgElement(svgPlancheTrace(cfg, panneaux), 'large'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    payload._meta.params = { theta: cfg.theta, k: cfg.k, bonneIndex, roles: panneaux.map((p) => p.role), signature: `${cfg.theta}-${cfg.k}-${cfg.cote}-${bonneIndex}-${panneaux.map((p) => p.role[0]).join('')}` };
    return payload;
};

// (Le V/F sur une figure candidate « Le bon tracé » a été retiré à la demande du prof, 2026-09-04.)

// ─── Carte 3a : lire la construction (QCM) ─────────────────────────────────

/** Point P sur l'axe, loin du pied H (≥ 40 px), dans le cadre. */
const pointSurAxe = (cfg, w = W, h = H) => {
    for (let essai = 0; essai < 100; essai++) {
        const s = randomChoice([1, -1]) * randInt(45, 90);
        const P = add(cfg.H, cfg.u, s);
        if (dansCadre(P, w, h, 22)) return P;
    }
    return null;
};

/** Segment AB qui coupe l'axe en I, avec son symétrique A′B′. */
const tirerSegmentCoupant = () => {
    for (let essai = 0; essai < 400; essai++) {
        const theta = randomChoice(ANGLES); const { u, n } = repere(theta);
        const C = [W / 2 + randInt(-10, 10), H / 2 + randInt(-6, 6)];
        const I = add(C, u, randInt(-25, 25));
        const cote = randomChoice([1, -1]);
        // B est ALIGNÉ avec A et I (retour prof 2026-09-04) : I est l'intersection exacte de [AB] et de (d)
        const A = add(add(I, u, randomChoice([1, -1]) * randInt(18, 40)), n, cote * randInt(35, 60));
        const mu = randInt(6, 14) / 10;
        const B = add(I, sub(A, I), -mu);
        const sym = (P) => { const s = dot(sub(P, C), n); return add(P, n, -2 * s); };
        const Ap = sym(A); const Bp = sym(B);
        if (![A, B, Ap, Bp].every((P) => dansCadre(P, W, H, 20))) continue;
        return { theta, u, n, C, I, A, B, Ap, Bp, cote };
    }
    throw new Error('gen_1071 : segment coupant introuvable');
};

const QUESTIONS_LECTURE = [
    {
        id: 'pied', figure: 'construction',
        q: 'Sur la figure, M′ est le symétrique de M par rapport à (d). Que représente le point H ?',
        bonne: 'Le milieu du segment [MM′], sur la droite (d).',
        leurres: ['Le symétrique du point M par rapport à (d).', 'Le milieu de la droite (d), entre ses deux extrémités.', 'Un point situé à la même distance de M et de M′ que de (d).'],
    },
    {
        id: 'mediatrice', figure: 'construction',
        q: 'Sur la figure, M′ est le symétrique de M par rapport à (d). Que représente la droite (d) pour le segment [MM′] ?',
        bonne: 'Sa médiatrice : elle lui est perpendiculaire et passe par son milieu.',
        leurres: ['Sa bissectrice : elle le partage en deux angles de même mesure.', 'Une parallèle : elle ne le coupe jamais, même prolongée.', 'Son support : le segment est porté par la droite (d).'],
    },
    {
        id: 'point_axe', figure: 'axe',
        q: 'Sur la figure, le point P appartient à la droite (d). Quel est le symétrique de P par rapport à (d) ?',
        bonne: 'Le point P lui-même : il n\'y a rien à construire.',
        leurres: ['Le point H, pied de la perpendiculaire issue de M.', 'Un point P′ situé de l\'autre côté de (d), à la même distance.', 'Le point M′, symétrique de M par rapport à (d).'],
    },
    {
        id: 'coupe', figure: 'coupant',
        q: 'Le segment [AB] coupe la droite (d) en I, et [A′B′] est son symétrique par rapport à (d). En quel point [A′B′] coupe-t-il (d) ?',
        bonne: 'En I : I est sur (d), donc son symétrique est I lui-même.',
        leurres: ['En I′, symétrique de I, situé de l\'autre côté de (d).', 'Au milieu du segment [A′B′], qui n\'est pas sur (d).', 'Il ne coupe pas (d) : il est entièrement de l\'autre côté.'],
    },
];

let derniereQuestionLecture1071 = null;

const svgLecture = (type) => {
    if (type === 'coupant') {
        const g = tirerSegmentCoupant();
        const { u, n, C, I, A, B, Ap, Bp, cote } = g;
        return svgFeuille({
            C, u, n,
            segments: [{ a: A, b: B, couleur: BLEU, ep: 2.2 }, { a: Ap, b: Bp, couleur: ORANGE, ep: 2.2, dash: true }],
            points: [
                { p: A, nom: 'A', couleur: BLEU, decal: decalOppose(A, I) }, { p: B, nom: 'B', couleur: BLEU, decal: decalOppose(B, I) },
                { p: Ap, nom: `A${PRIME}`, couleur: ORANGE, decal: decalOppose(Ap, I) }, { p: Bp, nom: `B${PRIME}`, couleur: ORANGE, decal: decalOppose(Bp, I) },
                // I est sur l'axe ET sur (AB) : l'étiquette va dans le PLUS LARGE des secteurs libres,
                // entre (IA) et (IB′) ou entre (IA) et (IA′)
                { p: I, nom: 'I', couleur: VERT, decal: (() => {
                    const dA = unit(sub(A, I)); const dAp = unit(sub(Ap, I)); const dBp = unit(sub(Bp, I));
                    const bis = dot(dA, dBp) < dot(dA, dAp) ? add(dA, dBp) : add(dA, dAp);
                    return unit(bis).map((v) => v * 12);
                })() },
            ],
            coteNomAxe: 1,
        }, ` data-axe="${cle(C)};${cleU(u)}"`);
    }
    const cfg = tirerConfig(randInt(2, 4), { marge: 24 });
    const fig = figureConstruction(cfg);
    if (type === 'axe') {
        const P = pointSurAxe(cfg);
        if (P) { fig.points.push({ p: P, nom: 'P', couleur: VERT, decal: add([0, 0], unit(sub(cfg.Mp, cfg.H)), 11) }); fig.loinDe = P; }
    }
    return svgFeuille(fig, ` data-axe="${cle(cfg.C)};${cleU(cfg.u)}"`);
};

const genLectureQcm = () => {
    let i = randInt(0, QUESTIONS_LECTURE.length - 1);
    if (i === derniereQuestionLecture1071) i = (i + 1) % QUESTIONS_LECTURE.length;
    derniereQuestionLecture1071 = i;
    const Q = QUESTIONS_LECTURE[i];
    const options = shuffle([Q.bonne, ...Q.leurres]);
    const payload = makeQcm(options, Q.bonne, `<p>${Q.q}</p>`, { ...opts(L_LECTURE), elements_additionnels: imgElement(svgLecture(Q.figure), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: Q.id };
    payload._meta.params = { question: Q.id, signature: Q.id };
    return payload;
};

// ─── Carte 3b : lire la construction (saisie de longueurs) ─────────────────

let derniereSignatureLectureSaisie1071 = null;

const genLectureSaisie = () => {
    let payload = construireLectureSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureLectureSaisie1071; t++) payload = construireLectureSaisie();
    derniereSignatureLectureSaisie1071 = payload._meta.params.signature;
    return payload;
};

const construireLectureSaisie = () => {
    const sousType = randomChoice(['double', 'moitie', 'segment']);
    const k = randInt(2, 5);   // 1 cm = 16 px : trop serré pour les étiquettes de la figure
    let texte; let reponse; let svg;
    if (sousType === 'segment') {
        // Longueur conservée : une figure (segment, triangle ou rectangle — formes ajoutées à la demande
        // du prof, 2026-09-04) d'un même côté de l'axe, son symétrique tracé ; la longueur d'un côté est
        // donnée, celle du côté symétrique demandée. Le côté connu est DESSINÉ à l'échelle (1 cm = 16 px).
        const forme = randomChoice(['segment', 'triangle', 'rectangle']);
        const long = randInt(2, 5);
        let g = null;
        for (let essai = 0; essai < 400 && !g; essai++) {
            const theta = randomChoice(ANGLES); const { u, n } = repere(theta);
            const C = [W / 2 + randInt(-10, 10), H / 2 + randInt(-6, 6)];
            const cote = randomChoice([1, -1]);
            // sommets en coordonnées (t le long de u, s perpendiculaire à l'axe) ; le côté connu relie iCote et jCote
            let coords; let iCote = 0; let jCote = 1;
            if (forme === 'segment') {
                const phi = rad(randInt(-40, 40)); const A = [randInt(-50, -10), randInt(1, 3) * SCALE];
                coords = [A, [A[0] + long * SCALE * Math.cos(phi), A[1] + long * SCALE * Math.sin(phi)]];
            } else if (forme === 'triangle') {
                // le côté connu AB est le côté ÉLOIGNÉ de l'axe, le troisième sommet C est entre AB et l'axe :
                // l'étiquette de longueur, à l'extérieur du triangle, reste loin de l'axe
                const phi = rad(randInt(-30, 30)); const A = [randInt(-55, -15), randInt(48, 60)];
                const B = [A[0] + long * SCALE * Math.cos(phi), A[1] + long * SCALE * Math.sin(phi)];
                coords = [A, B, [A[0] + randInt(0, long * SCALE), randInt(SCALE, Math.min(A[1], B[1]) - 30)]];
            } else {
                // le côté connu est CD, le côté éloigné de l'axe (même raison)
                const w = long * SCALE; const h = randInt(2, 3) * SCALE; const A = [randInt(-55, -25), randInt(1, 2) * SCALE];
                coords = [A, [A[0] + w, A[1]], [A[0] + w, A[1] + h], [A[0], A[1] + h]];
                iCote = 2; jCote = 3;
            }
            if (coords.some(([, s]) => s < SCALE)) continue;
            const pts = coords.map(([t, s]) => add(add(C, u, t), n, cote * s));
            const sym = coords.map(([t, s]) => add(add(C, u, t), n, -cote * s));
            if (![...pts, ...sym].every((P) => dansCadre(P, W, H, 22))) continue;
            g = { theta, u, n, C, pts, sym, cote, iCote, jCote };
        }
        if (!g) throw new Error('gen_1071 : figure introuvable');
        const { u, n, C, pts, sym, cote, iCote, jCote } = g;
        const nb = pts.length;
        const noms = forme === 'segment' ? randomChoice([['M', 'N'], ['A', 'B'], ['R', 'S']])
            : forme === 'triangle' ? randomChoice([['A', 'B', 'C'], ['R', 'S', 'T'], ['E', 'F', 'G']])
                : randomChoice([['A', 'B', 'C', 'D'], ['E', 'F', 'G', 'H'], ['R', 'S', 'T', 'U']]);
        const prime = (nom) => `${nom}${PRIME}`;
        const centre = pts.reduce((acc, P) => add(acc, P, 1 / nb), [0, 0]); const centreSym = sym.reduce((acc, P) => add(acc, P, 1 / nb), [0, 0]);
        const vers = [n[0] * cote, n[1] * cote];   // de l'axe vers la figure
        const mil = add(pts[iCote], sub(pts[jCote], pts[iCote]), 0.5);
        // « long cm » perpendiculairement au côté connu, vers l'extérieur de la figure (côté opposé à l'axe pour un segment)
        const dCote = unit(sub(pts[jCote], pts[iCote])); let pCote = [-dCote[1], dCote[0]];
        const ext = forme === 'segment' ? vers : sub(mil, centre);
        if (dot(pCote, ext) < 0) pCote = [-pCote[0], -pCote[1]];
        const segments = [];
        for (let i = 0; i < nb; i++) {
            const j = (i + 1) % nb; if (forme === 'segment' && i === 1) break;
            segments.push({ a: pts[i], b: pts[j], couleur: BLEU, ep: 2.2 }, { a: sym[i], b: sym[j], couleur: ORANGE, ep: 2.2, dash: true });
        }
        // les pointillés point → symétrique ne sont tracés que pour un segment (ils traverseraient un polygone)
        if (forme === 'segment') segments.push({ a: pts[iCote], b: sym[iCote], couleur: '#94a3b8', ep: 1, dash: true }, { a: pts[jCote], b: sym[jCote], couleur: '#94a3b8', ep: 1, dash: true });
        const points = [];
        pts.forEach((P, i) => points.push({ p: P, nom: noms[i], couleur: BLEU, decal: forme === 'segment' ? vers.map((v) => v * 12) : decalOppose(P, centre) }));
        sym.forEach((P, i) => points.push({ p: P, nom: prime(noms[i]), couleur: ORANGE, decal: forme === 'segment' ? vers.map((v) => -v * 12) : decalOppose(P, centreSym) }));
        svg = svgFeuille({
            C, u, n, segments, points,
            textes: [{ p: add(mil, pCote, 15), txt: `${long} cm`, couleur: BLEU, gras: true }],
            coteNomAxe: -cote,
        }, ` data-axe="${cle(C)};${cleU(u)}" data-longueur="${long}" data-figure="${pts.map(cle).join('|')}" data-cote="${noms[iCote]}${noms[jCote]}"`);
        const nomFig = noms.join(''); const nomFigP = noms.map(prime).join('');
        const S = `${noms[iCote]}${noms[jCote]}`; const Sp = `${prime(noms[iCote])}${prime(noms[jCote])}`;
        if (forme === 'segment') texte = `<p>Sur la figure, le segment [${Sp}] est le symétrique du segment [${S}] par rapport à la droite (d). Le segment [${S}] mesure ${long} cm.</p><p>Combien mesure le segment [${Sp}] ?</p>`;
        else texte = `<p>Sur la figure, le ${forme} ${nomFigP} est le symétrique du ${forme} ${nomFig} par rapport à la droite (d). Le côté [${S}] mesure ${long} cm.</p><p>Combien mesure le côté [${Sp}] ?</p>`;
        reponse = long;
        const payload = makeSaisie(String(reponse), texte, { ...opts(L_LECTURE), suffixe: 'cm', elements_additionnels: imgElement(svg, 'medium') });
        payload.contenu = { ...payload.contenu, sousType };
        payload._meta.params = { sousType, forme, long, cote: S, signature: `${sousType}-${forme}-${S}-${long}-${g.theta}` };
        return payload;
    }
    const cfg = tirerConfig(k, { marge: 24 });
    if (sousType === 'double') {
        svg = svgFeuille(figureConstruction(cfg, { longueurs: { HM: k } }), ` data-axe="${cle(cfg.C)};${cleU(cfg.u)}"`);
        texte = `<p>Sur la figure, M′ est le symétrique de M par rapport à la droite (d), et H est le pied de la perpendiculaire. Le point M est à ${k} cm de (d).</p><p>Combien mesure le segment [MM′] ?</p>`;
        reponse = 2 * k;
    } else {
        // MM′ donné en légende, dans le coin le plus éloigné de l'axe ; HM′ demandé
        const fig = figureConstruction(cfg);
        const { u, n, C } = cfg;
        const coins = [[10, 14, 'start'], [W - 10, 14, 'end'], [10, H - 8, 'start'], [W - 10, H - 8, 'end']];
        const loin = coins.map((c) => ({ c, d: Math.abs(dot(sub([c[0], c[1]], C), n)) })).sort((a, b) => b.d - a.d)[0].c;
        fig.textes.push({ p: [loin[0], loin[1]], txt: `MM${PRIME} = ${2 * k} cm`, couleur: '#334155', gras: true, taille: 12, anchor: loin[2] });
        svg = svgFeuille(fig, ` data-axe="${cle(cfg.C)};${cleU(cfg.u)}"`);
        texte = `<p>Sur la figure, M′ est le symétrique de M par rapport à la droite (d), et H est le pied de la perpendiculaire. Le segment [MM′] mesure ${2 * k} cm.</p><p>Combien mesure le segment [HM′] ?</p>`;
        reponse = k;
    }
    const payload = makeSaisie(String(reponse), texte, { ...opts(L_LECTURE), suffixe: 'cm', elements_additionnels: imgElement(svg, 'medium') });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, k, reponse, signature: `${sousType}-${k}-${cfg.theta}-${cfg.cote}` };
    return payload;
};

// ─── Carte 3c : lire la construction (V/F sur la figure) ───────────────────

let dernieresLignesLectureVf1071 = [];

const genLectureVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const k = randInt(2, 5);   // 1 cm = 16 px : trop serré pour les étiquettes de la figure
        const cfg = tirerConfig(k, { marge: 26 });
        const P = pointSurAxe(cfg);
        if (!P) continue;
        const fig = figureConstruction(cfg, { longueurs: { HM: k } });
        fig.points.push({ p: P, nom: 'P', couleur: VERT, decal: unit(sub(cfg.Mp, cfg.H)).map((v) => v * 11) });
        fig.loinDe = P;   // (d) à l'extrémité opposée à P
        const nomMp = `M${PRIME}`;
        const candidates = [
            { l: `H est le milieu du segment M${nomMp}.`, v: true },
            { l: `La droite (MH) est perpendiculaire à (d).`, v: true },
            { l: `La droite (d) est la médiatrice du segment M${nomMp}.`, v: true },
            { l: `Les longueurs HM et H${nomMp} sont égales.`, v: true },
            { l: `Le symétrique de P par rapport à (d) est P lui-même.`, v: true },
            { l: `Le segment M${nomMp} mesure ${2 * k} cm.`, v: true },
            { l: `${nomMp} est à ${k} cm de la droite (d).`, v: true },
            { l: `${nomMp} est à ${2 * k} cm de la droite (d).`, v: false },
            // jamais 2k (revue 2026-09-04, A1 : k + 2 valait 2k pour k = 2) ; k est la confusion MM′ ↔ distance à l'axe
            { l: `Le segment M${nomMp} mesure ${randomChoice([k, 2 * k - 1, 2 * k + 1])} cm.`, v: false },
            { l: `Le symétrique de P par rapport à (d) est un point P${PRIME} différent de P.`, v: false },
            { l: `H est le symétrique de M par rapport à (d).`, v: false },
            { l: `La droite (d) est parallèle à la droite (M${nomMp}).`, v: false },
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        // familles : P (4, 9), MM′ (5, 8), distance de M′ (6, 7), perpendiculaire/parallèle (1, 11)
        const familles = [[4, 9], [5, 8], [6, 7], [1, 11]];
        if (familles.some(([i, j]) => lignes.includes(candidates[i]) && lignes.includes(candidates[j]))) continue;
        if (essai > 120) dernieresLignesLectureVf1071 = [];
        if (lignes.some((it) => dernieresLignesLectureVf1071.includes(it.l))) continue;
        dernieresLignesLectureVf1071 = lignes.map((it) => it.l);
        const svg = svgFeuille(fig, ` data-axe="${cle(cfg.C)};${cleU(cfg.u)}"`);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>Sur la figure, M${PRIME} est le symétrique de M par rapport à la droite (d), H est le pied de la perpendiculaire et P est un point de (d). Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            { ...opts(L_LECTURE), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { theta: cfg.theta, k };
        return payload;
    }
    throw new Error('gen_1071 : V/F lecture sans mixité après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    gestes_qcm_unique: genGestesQcm,
    gestes_texte_trou: genGestesTrou,
    gestes_tableau_vf: genGestesVf,
    trace_qcm_unique: genTraceQcm,
    lecture_qcm_unique: genLectureQcm,
    lecture_saisie: genLectureSaisie,
    lecture_tableau_vf: genLectureVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou: 'Texte à trous' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'gestes', label: L_GESTES, formatsList: buildFormats('gestes') },
    { id: 'trace', label: L_TRACE, formatsList: buildFormats('trace') },
    { id: 'lecture', label: L_LECTURE, formatsList: buildFormats('lecture') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
